import { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Apple, CheckCircle2, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { INSTALL_HELP, useInstallPrompt } from "@/lib/pwa";
import { track } from "@/lib/track";

const AppleSvg = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" aria-hidden="true"><path d="M16.37 12.6c-.02-2.2 1.8-3.26 1.88-3.31-1.03-1.5-2.62-1.7-3.19-1.73-1.36-.14-2.65.8-3.34.8-.69 0-1.75-.78-2.88-.76-1.48.02-2.85.86-3.61 2.19-1.54 2.67-.39 6.62 1.1 8.79.73 1.06 1.6 2.25 2.74 2.2 1.1-.04 1.51-.71 2.84-.71 1.32 0 1.7.71 2.86.69 1.18-.02 1.93-1.08 2.65-2.14.84-1.23 1.18-2.42 1.2-2.48-.03-.01-2.3-.88-2.32-3.5zM14.18 6.15c.6-.73 1.01-1.75.9-2.76-.87.04-1.92.58-2.54 1.31-.56.65-1.05 1.68-.92 2.67.97.08 1.96-.49 2.56-1.22z" /></svg>
);

// Pulsante "SCARICA APP": installa la web app direttamente da questo sito (nessuno store)
const WebAppButton = ({ id }) => {
  const { canInstall, install, installed } = useInstallPrompt();
  const ios = id === "ios";
  if (installed) return <p data-testid={`app-installed-${id}`} className="mt-auto flex items-center gap-2 text-sm font-medium text-slate-900"><CheckCircle2 className="h-4 w-4 text-emerald-600" /> App già installata</p>;
  const onClick = async () => {
    track("click_scarica_app", { platform: id });
    if (!ios && canInstall && (await install())) return;
    toast("Installa Apollo.AI", { description: INSTALL_HELP[id] });
  };
  return (
    <button type="button" data-testid={`app-store-button-${id}`} onClick={onClick}
      className="mt-auto flex w-full items-center justify-center gap-3 bg-slate-900 px-5 py-4 text-base font-bold tracking-wide text-white transition-colors hover:bg-emerald-500 hover:text-slate-900">
      {ios ? <AppleSvg /> : <Smartphone className="h-5 w-5" />} SCARICA APP
      <span className="text-xs font-medium opacity-70">Web App</span>
    </button>
  );
};

const CARDS = [
  { id: "android", title: "Android", icon: Smartphone,
    steps: ["Inquadra il QR code o apri il sito con Chrome", "Tocca \"Installa app\" (oppure menu \u22ee \u2192 Installa app)", "Apollo.AI compare tra le tue app"] },
  { id: "ios", title: "iPhone e iPad", icon: Apple,
    steps: ["Inquadra il QR code e apri il sito con Safari", "Tocca Condividi \u2192 \"Aggiungi alla schermata Home\"", "Apollo.AI compare nella schermata Home"] },
];

export const AppDownload = () => {
  const [url, setUrl] = useState("");
  useEffect(() => {
    setUrl(`${window.location.origin}/?installa=1`);
  }, []);
  return (
    <section id="app" className="border-t border-slate-200 bg-white py-20 lg:py-28">
      <div className="mx-auto grid max-w-7xl gap-12 px-5 lg:grid-cols-12 lg:px-8">
        <div className="lg:col-span-5">
          <span className="eyebrow">Web App Apollo.AI</span>
          <h2 className="section-title mt-5">Scarica l'App PixaSolar — Fotovoltaico Smart</h2>
          <p className="mt-5 max-w-md text-base text-slate-600">
            Installa Apollo.AI direttamente da questo sito, senza store: scansiona il QR code o tocca "SCARICA APP".
          </p>
        </div>
        <div className="grid gap-5 sm:grid-cols-2 lg:col-span-7">
          {CARDS.map((c) => (
            <div key={c.id} data-testid={`app-card-${c.id}`} className="flex flex-col border border-slate-200 p-7">
              <div className="flex items-center justify-between">
                <c.icon className="h-6 w-6 text-slate-900" />
                <span className="text-xs font-semibold uppercase tracking-widest text-slate-400">Installazione diretta</span>
              </div>
              <div className="mt-6 self-start border border-slate-200 bg-white p-3" data-testid={`app-qr-${c.id}`}>
                {url && <QRCodeSVG value={url} size={148} fgColor="#0F172A" />}
              </div>
              <p className="mt-6 text-xl font-semibold text-slate-900">Installa su {c.title}</p>
              <ol className="mt-3 space-y-1.5 text-sm text-slate-600">
                {c.steps.map((s, i) => <li key={s}><span className="mr-2 font-semibold text-slate-900">{i + 1}.</span>{s}</li>)}
              </ol>
              <div className="h-6" />
              <WebAppButton id={c.id} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
